import React, { useCallback, useEffect, useState } from 'react'

import { Card } from 'components'
import { useLoading, useModal, useAuth, usePersistForm } from 'hooks'
import useMovement from 'hooks/useMovement'
import { useForm } from 'react-hook-form'
import { MovimentacaoMensalServices } from 'services/Supplier/MovMensal'
import { COLORS, FORMAT, SPACING } from 'utils'

import {
  RequestGetPesquisaAnalista,
  GetPesquisaAnalista,
  ITipeAction
} from '../../types'
import { FiltersPesquisa, TablePesquisaMov } from './components'
import { IOnChangeUnidadeGestora } from 'components/UnidadeGestoraFields'

export interface FormEfetivoPesquisa {
  PageNumber?: number
  PageSize?: number
  cdContrato?: string
  mesAnoInicio?: number
  mesAnoFim?: number
  nmFornecedor?: string
  cpf?: string
  nmColaborador?: string
  flMovInexistente?: string
  flStatusMobilizacao?: string
  flContraparte?: boolean
  flTodos?: boolean
  flAnalista?: boolean
  idUnidadeGestora?: IOnChangeUnidadeGestora
}

const DEFAULT_FORM: FormEfetivoPesquisa = {
  PageNumber: 1,
  PageSize: 5,
  cdContrato: '',
  cpf: '',
  flAnalista: false,
  flContraparte: false,
  flMovInexistente: 'null',
  flStatusMobilizacao: '4',
  flTodos: true,
  mesAnoFim: null,
  mesAnoInicio: null,
  nmColaborador: '',
  nmFornecedor: '',
  idUnidadeGestora: {
    Diretoria: null,
    DiretoriaExecutiva: null,
    Gerencia: null,
    GerenciaGeral: null
  }
}

export interface IPesquisaMovAnalista {
  handleChangeTab: (_competencia: string) => void
}
const PesquisaMovAnalista: React.FC<IPesquisaMovAnalista> = ({
  handleChangeTab
}) => {
  const { user } = useAuth()
  const { setSelectedMovement, resetContext } = useMovement()
  const { setLoading } = useLoading()
  const { showModal } = useModal()

  const [pageCount, setPageCount] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(15)
  const [page, setPage] = React.useState<number>(1)

  const { control, reset, handleSubmit, watch, setValue } =
    useForm<FormEfetivoPesquisa>({
      defaultValues: DEFAULT_FORM
    })

  usePersistForm('movimentacao-mensal-analista-form', {
    exclude: ['idUnidadeGestora'],
    setValue,
    watch
  })

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
  }
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(1)
  }

  const [listaPesquisa, setListaPesquisa] = useState<
    GetPesquisaAnalista[] | undefined
  >()

  const handleFilter = useCallback(async () => {
    setLoading('loading')
    setListaPesquisa([])
    setPageCount(0)
    const form = watch()

    try {
      const objEnvio = {
        PageNumber: page,
        PageSize: rowsPerPage,
        UnidadeGestoraId: form.idUnidadeGestora ?? {
          Diretoria: null,
          DiretoriaExecutiva: null,
          Gerencia: null,
          GerenciaGeral: null
        },
        UsuarioId: user.IdUsuario,
        flAnalista: form.flAnalista,
        flContraparte: form.flContraparte,
        flMovInexistente: form.flMovInexistente === 'null' ? null : form.flMovInexistente === '0' ? false : true,
        flTodos: form.flTodos,
        ...(form.cdContrato && { cdContrato: form.cdContrato.toUpperCase() }),
        ...(form?.cpf && { cpf: FORMAT.unMask(form?.cpf) }),
        ...(!['', '4'].includes(form?.flStatusMobilizacao) && {
          flStatusMobilizacao: form.flStatusMobilizacao
        }),
        ...(form?.mesAnoFim && {
          mesAnoFim: parseInt(FORMAT.formatYYYYMM(form?.mesAnoFim))
        }),
        ...(form?.mesAnoInicio && {
          mesAnoInicio: parseInt(FORMAT.formatYYYYMM(form?.mesAnoInicio))
        }),
        ...(form?.nmColaborador && { nmColaborador: form.nmColaborador }),
        ...(form?.nmFornecedor && { nmFornecedor: form.nmFornecedor })
      } as RequestGetPesquisaAnalista

      const response =
        await MovimentacaoMensalServices().getPesquisaMovAnalista(objEnvio)

      if (response.status !== 200)
        throw new Error('Não foi possivel obter dados.')

      setListaPesquisa(response.data.entity.items)
      setPageCount(response.data.entity.pageCount)
      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Não foi possivel obter movimentações',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }, [page, rowsPerPage])

  const resetForm = () => {
    reset()
    page > 1 && setPage(1)
    handleFilter()
  }

  useEffect(() => {
    handleFilter()
  }, [handleFilter])

  useEffect(resetContext, [])

  const handleClickMov = (
    competencia: string,
    tipo: ITipeAction,
    data: GetPesquisaAnalista
  ) => {
    handleChangeTab(competencia)

    setSelectedMovement({
      cdContrato: data.cdContrato,
      cdContratoPrincipal: data.cdContratoPrincipal,
      contratoId: data.contratoId,
      dtVigenciaFim: data.dtVigenciaFim,
      dtVigenciaInicio: data.dtVigenciaInicio,
      flCompAtual: data.flCompAtual,
      fornecedorId: data.fornecedorId,
      mesAno: data.mesAno,
      nmEmpresaFornecedor: data.nmFornecedor,
      nmEmpresaFornecedorPrincipal: data.nmFornecedorPrincipal,
      txObjetoContrato: data.txObjetoContrato
    })
  }

  return (
    <Card
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      padding={SPACING.spacing20}
      marginBottom={SPACING.spacing100}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <FiltersPesquisa
        control={control}
        reset={resetForm}
        watch={watch}
        page={page}
        setValue={setValue}
        setPage={setPage}
        handleSubmit={handleSubmit(handleFilter)}
      />
      <TablePesquisaMov
        tableData={listaPesquisa}
        handleAddNewTab={handleClickMov}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        setListaPesquisa={setListaPesquisa}
        rowsPerPage={rowsPerPage}
        page={page}
        pageCount={pageCount}
      />
    </Card>
  )
}

export default PesquisaMovAnalista